import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Block } from '@/types';
import styles from '../ShutdownRitual.module.css';

interface ReviewStepProps {
  completedBlocks: Block[];
  plannedBlocks: Block[];
  totalBlocks: Block[];
}

function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return format(date, 'h:mm a');
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function ReviewStep({ completedBlocks, plannedBlocks, totalBlocks }: ReviewStepProps) {
  const completionRate =
    totalBlocks.length > 0 ? Math.round((completedBlocks.length / totalBlocks.length) * 100) : 0;

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Review Your Day
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        Let us look at what you accomplished today and what still needs attention.
      </motion.p>

      {/* Stats Grid */}
      <motion.div className={styles.statsGrid} variants={itemVariants}>
        <div className={styles.statCard}>
          <p className={styles.statNumber}>{completedBlocks.length}</p>
          <p className={styles.statLabel}>Blocks Completed</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statNumber}>{plannedBlocks.length}</p>
          <p className={styles.statLabel}>Still Pending</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statNumber}>{totalBlocks.length}</p>
          <p className={styles.statLabel}>Total Blocks</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statNumber}>{completionRate}%</p>
          <p className={styles.statLabel}>Completion Rate</p>
        </div>
      </motion.div>

      {/* Completed Blocks */}
      {completedBlocks.length > 0 && (
        <motion.div variants={itemVariants}>
          <h3
            style={{
              fontSize: '14px',
              color: '#68d391',
              marginBottom: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Completed
          </h3>
          <div className={styles.blockList}>
            {completedBlocks.map((block, index) => (
              <motion.div
                key={block.id}
                className={styles.blockItem}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className={styles.blockColor}
                  style={{ backgroundColor: block.color }}
                  aria-hidden="true"
                />
                <div className={styles.blockInfo}>
                  <p className={styles.blockTitle}>{block.title}</p>
                  <p className={styles.blockTime}>
                    {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
                  </p>
                </div>
                <span className={`${styles.blockStatus} ${styles.blockStatusCompleted}`}>Done</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Pending Blocks */}
      {plannedBlocks.length > 0 && (
        <motion.div variants={itemVariants} style={{ marginTop: '24px' }}>
          <h3
            style={{
              fontSize: '14px',
              color: '#fc8181',
              marginBottom: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Still Pending
          </h3>
          <div className={styles.blockList}>
            {plannedBlocks.map((block, index) => (
              <motion.div
                key={block.id}
                className={styles.blockItem}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className={styles.blockColor}
                  style={{ backgroundColor: block.color }}
                  aria-hidden="true"
                />
                <div className={styles.blockInfo}>
                  <p className={styles.blockTitle}>{block.title}</p>
                  <p className={styles.blockTime}>
                    {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
                  </p>
                </div>
                <span className={`${styles.blockStatus} ${styles.blockStatusMissed}`}>Pending</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {totalBlocks.length === 0 && (
        <motion.div className={styles.emptyState} variants={itemVariants}>
          <p>No blocks scheduled for today. That is okay - tomorrow is a new opportunity!</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default ReviewStep;
